import { PropsWithChildren, useMemo, useState } from 'react';
import { useDocumentState, useDocumentUpdater } from '../../context';
import classNames from 'classnames';
import styles from './styles.module.scss';
import { resolveConsentTranslations } from './translation';

type VerificationProps = PropsWithChildren<{
  envelopeId: string;
}>;

const Verification = ({ children }: VerificationProps) => {
  const [emailAddress, setEmailAddress] = useState('');
  const [consentCheck, setConsentCheck] = useState(false);
  const [errMessage, setErrMessage] = useState('');
  const [errors, setErrors] = useState<any>({});
  const verified = useDocumentState((s) => s.verified);
  const fetchDocumentVerification = useDocumentUpdater(
    (s) => s.fetchDocumentVerification
  );
  const currentIndex = useDocumentState((s) => s.currentIndex);
  const documents = useDocumentState((s) => s.documents);
  const omitVerifyEmail = documents.every((doc) => !doc.verifyEmail);

  const translation = useMemo(
    () =>
      resolveConsentTranslations(documents[currentIndex]?.consentTranslations),
    [documents, currentIndex]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailAddress(e.target.value);
  };

  const handleSubmit = async () => {
    setErrMessage('');
    setErrors({});

    if (!omitVerifyEmail && !emailAddress) {
      setErrMessage(translation.email_error);
      setErrors({ email: true });
      return;
    }

    if (!consentCheck) {
      setErrMessage(translation.consent_error);
      return;
    }

    try {
      let currentDocumentEnvelopeID = documents[currentIndex].id;
      const value = await fetchDocumentVerification(
        currentDocumentEnvelopeID,
        consentCheck,
        omitVerifyEmail ? '' : emailAddress
      );

      if (!value) {
        setErrMessage(translation.auth_error);
      }
    } catch (err: any) {
      setErrMessage(err?.message ?? translation.auth_error);
    }
  };

  if (verified) {
    return <>{children}</>;
  }

  return (
    <>
      <div className={styles.container}>
        <div className={styles.content}>
          <h2>
          {omitVerifyEmail
            ? translation.consent_title
            : translation.verification_title
          }
          </h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              return handleSubmit();
            }}
          >
            {!omitVerifyEmail &&
              <input
                type='email'
                className={classNames({ [styles.error]: errors.email })}
                placeholder={translation.email_placeholder}
                value={emailAddress}
                onChange={handleChange}
              />
            }
            <div className={styles.consent}>
              <label className={styles.consentCheck}>
                {translation.consent_text}
                <input
                  type='checkbox'
                  id='consent'
                  name='consent'
                  checked={consentCheck}
                  onChange={() => setConsentCheck(!consentCheck)}
                />
                <span className={styles.checkmark}></span>
              </label>
            </div>
            {errMessage && <div className={styles.alert}>{errMessage}</div>}
            <button type='submit' className={styles.continueButton}>
              {translation.continue_button}
            </button>
          </form>
        </div>
      </div>
      {children}
    </>
  );
};

export default Verification;
